import React from 'react';
import styled from 'styled-components';
import useLocale from '../../hooks/useLocale';
import AuspiciousnessBar from './AuspiciousnessBar';
import RemedySection from './RemedySection';

const Card = styled.article`
  max-width: 640px;
  margin: 0 auto;
  padding: ${p => p.theme.spacing[5]}px;
  background: linear-gradient(180deg, #FFFBF0, #FFE8D0);
  border: 1px solid rgba(239,108,0,0.3);
  border-radius: 16px;
  box-shadow: 0 6px 24px rgba(139,58,0,0.08);
  text-align: center;

  @media (max-width: ${p => p.theme.breakpoints.tablet}px) {
    padding: ${p => p.theme.spacing[4]}px;
  }
`;

const Motif = styled.div`
  font-size: 36px;
  margin-bottom: ${p => p.theme.spacing[2]}px;
  line-height: 1;
`;

const Heading = styled.h2`
  font-family: 'Noto Sans Tamil', 'Playfair Display', serif;
  font-size: 22px;
  color: #8B3A00;
  margin: 0 0 ${p => p.theme.spacing[4]}px;
`;

const CellHeader = styled.div`
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
  font-size: ${p => p.theme.typography.sizes.lg};
  color: #5D2A00;
  font-weight: 600;
  margin-bottom: ${p => p.theme.spacing[2]}px;
`;

const SyllableDisplay = styled.span`
  font-family: 'Noto Sans Devanagari', serif;
  font-size: 32px;
  font-weight: 700;
  color: #8B3A00;
  margin-left: 6px;
  vertical-align: middle;
`;

const VerseDev = styled.p`
  font-family: 'Noto Sans Devanagari', serif;
  font-size: 18px;
  line-height: 1.8;
  color: #2C1810;
  margin: ${p => p.theme.spacing[4]}px 0 ${p => p.theme.spacing[2]}px;
`;

const VerseTa = styled.p`
  font-family: 'Noto Sans Tamil', sans-serif;
  font-size: 15px;
  line-height: 1.7;
  color: ${p => p.theme.colors.text.secondary};
  margin: 0 0 ${p => p.theme.spacing[4]}px;
`;

const InterpretationHeading = styled.h3`
  font-family: 'Noto Sans Tamil', 'Playfair Display', serif;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #8B3A00;
  margin: ${p => p.theme.spacing[5]}px 0 ${p => p.theme.spacing[2]}px;
`;

const InterpretationPrimary = styled.p`
  font-family: 'Noto Sans Tamil', sans-serif;
  font-size: 16px;
  line-height: 1.7;
  color: #2C1810;
  margin: 0 0 ${p => p.theme.spacing[3]}px;
`;

const InterpretationSecondary = styled.p`
  font-family: ${p => p.theme.typography.fontFamily};
  font-size: 14px;
  line-height: 1.6;
  color: ${p => p.theme.colors.text.secondary};
  font-style: italic;
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing[3]}px;
  justify-content: center;
  margin-top: ${p => p.theme.spacing[5]}px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    flex-direction: column;
  }
`;

const ShareButton = styled.button`
  padding: 12px 32px;
  background: linear-gradient(135deg, #EF6C00, #D84315);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
`;

const ReturnButton = styled.button`
  padding: 12px 32px;
  background: none;
  border: 1px solid #EF6C00;
  color: #8B3A00;
  border-radius: 8px;
  cursor: pointer;
  min-height: 44px;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
  &:hover { background: rgba(239,108,0,0.08); }
`;

const GaneshaResultCard = ({ cellIndex, reading, onShare, onReturn, lang }) => {
  const t = useLocale(lang, 'ganeshaPrashnavali');
  const primaryInterp = lang === 'ta' ? reading.interpretation_ta : reading.interpretation_en;
  const secondaryInterp = lang === 'ta' ? reading.interpretation_en : reading.interpretation_ta;
  const cellLabelTemplate = lang === 'ta'
    ? (t.cellLabelTa || 'கட்டம் {n}')
    : (t.cellLabelEn || 'Cell {n}');
  const cellLabel = cellLabelTemplate.replace('{n}', cellIndex);
  const syllable = lang === 'ta' ? reading.syllable_ta : reading.syllable;

  return (
    <Card>
      <Motif aria-hidden="true">🕉️</Motif>
      <Heading>{t.resultHeading || "Sri Ganesha's Guidance"}</Heading>

      <CellHeader>
        {cellLabel} · <SyllableDisplay>{syllable}</SyllableDisplay>
      </CellHeader>

      <AuspiciousnessBar level={reading.auspiciousness} lang={lang} />

      <VerseDev>{reading.verse}</VerseDev>
      <VerseTa>{reading.verse_ta}</VerseTa>

      <InterpretationHeading>{t.interpretationHeading || "Ganapathi's Message"}</InterpretationHeading>
      <InterpretationPrimary>{primaryInterp}</InterpretationPrimary>
      <InterpretationSecondary>{secondaryInterp}</InterpretationSecondary>

      <RemedySection
        auspiciousness={reading.auspiciousness}
        remedy_ta={reading.remedy_ta}
        remedy_en={reading.remedy_en}
        lang={lang}
      />

      <Actions>
        <ShareButton onClick={onShare}>{t.shareButton || 'Share'}</ShareButton>
        <ReturnButton onClick={onReturn}>{t.returnButton || 'Return'}</ReturnButton>
      </Actions>
    </Card>
  );
};

export default GaneshaResultCard;
